/**
 * InlineDdlDatabaseBody — the embeddable content (parked-statement scan +
 * `DatabaseEditFormBody`, or the engine-unsupported notice) for a parked
 * CREATE DATABASE / CREATE SCHEMA (mysql) / ALTER DATABASE statement,
 * consumed directly by the Compact DDL editor's `compactDdlBodyArm`.
 *
 * Mode selection (`create` vs `alter`) is driven by the parked statement's
 * leading keyword:
 *   - CREATE [PLUGGABLE] {DATABASE|SCHEMA} …  → create mode
 *   - ALTER  [PLUGGABLE] {DATABASE|SCHEMA} …  → alter mode
 *
 * Engine gating: sqlite databases are filesystem files — renders a muted
 * one-line notice with the documented redirect copy instead of the form.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice G1.
 */
import { useMemo } from 'react';

import {
  getCreateDatabaseUnsupportedTooltip,
  isCreateDatabaseSupported,
} from '../edit/edits/createDatabase';
import type { VqeDdlEditContext } from '../edit/editContext';
import { DatabaseEditFormBody } from '../render/common/DatabaseEditFormBody';
import { valuesFromParsed } from '../render/common/databaseFormHelpers';
import {
  parseParkedDatabaseStatement,
  type ParsedParkedDatabase,
} from './parseParkedDatabaseStatement';

export interface InlineDdlDatabaseBodyProps {
  readonly editContext: VqeDdlEditContext;
  /** Test seam — overrides the parsed parked-database (skips the scan). */
  readonly parsedOverride?: ParsedParkedDatabase | null;
  /** Invoked after a successful commit or cancel, so an embedding popover can close. */
  readonly onDone?: () => void;
  /**
   * Cancel-only escape, when the host wants Cancel and commit to mean
   * DIFFERENT things. Falls back to `onDone` when omitted, so every existing
   * caller keeps the old "commit or cancel both call `onDone`" behaviour.
   * The compact popover passes it to close on Cancel WITHOUT closing on a
   * successful commit (the commit funnels through the app-shell
   * PreviewDialog, which needs this body’s context to stay mounted).
   */
  readonly onCancel?: () => void;
}

export function InlineDdlDatabaseBody({
  editContext,
  parsedOverride,
  onDone,
  onCancel,
}: InlineDdlDatabaseBodyProps) {
  const engine = editContext.service.engineId;

  const parsed = useMemo<ParsedParkedDatabase | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      const text = buf.parsedDocument.source.slice(
        stmt.range.startOffset,
        stmt.range.endOffset,
      );
      return parseParkedDatabaseStatement(text);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);

  const mode: 'create' | 'alter' = parsed?.action === 'alter' ? 'alter' : 'create';
  const initialValues = parsed ? valuesFromParsed(parsed) : undefined;

  if (!isCreateDatabaseSupported(engine)) {
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid="inline-ddl-database-unsupported"
      >
        {getCreateDatabaseUnsupportedTooltip(engine)}
      </p>
    );
  }

  return (
    <DatabaseEditFormBody
      editContext={editContext}
      mode={mode}
      initialValues={initialValues}
      testId="inline-ddl-database-form"
      onCommitted={() => onDone?.()}
      onCancel={() => (onCancel ?? onDone)?.()}
    />
  );
}
